import { postJson } from "./api";
import { devLog, devWarn } from "./utils/devLog";

const REGISTER_PATH = "register-owner";
const LOGIN_PATH    = "login";

function extractUserAndId(data) {
  const user =
    data?.user ??
    data?.data?.user ??
    (typeof data?.data === "object" && !Array.isArray(data.data) ? data.data : null) ??
    null;

  const id =
    user?.id ??
    user?.user_id ??
    data?.id ??
    data?.user_id ??
    data?.data?.id ??
    null;

  return { user, id };
}

export async function registerOwner({
  name, email, phone_number, password, password_confirmation, username, role = "owner",
}) {
  const payload = {
    role,
    username: username ?? (email?.split("@")[0] || `user_${Date.now()}`),
    name,
    email,
    phone_number,
    password,
    password_confirmation,
  };

  const data = await postJson(REGISTER_PATH, payload);

  // try to normalize various API shapes
  const { user, id } = extractUserAndId(data);

  // If no id but API says success, still consider it ok
  if (!id && !(data?.status === true || data?.success === true || data?.message)) {
    devLog("[registerOwner] Raw API response (no id):", data);
    throw new Error("API returned no user id — record may not be saved.");
  }

  let token = data?.token || data?.access_token || data?.data?.token;
  if (!token && data?.authorization) {
    token = data.authorization;
  }
  if (!token && typeof data === 'string') {
    token = data;
  }

  if (token) localStorage.setItem("auth_token", token);
  if (user)        localStorage.setItem("auth_user", JSON.stringify(user));

  return data;
}

export async function login({ email, password }) {
  const data = await postJson(LOGIN_PATH, { email, password });
  
  devLog("🔐 Login API Response:", {
    hasData: !!data,
    keys: data ? Object.keys(data) : [],
    token: data?.token,
    access_token: data?.access_token,
    data_token: data?.data?.token,
    full_response: data
  });

  const user  = data?.user || data?.data?.user || data?.data;
  let token = data?.token || data?.access_token || data?.data?.token;
  
  // Check if token might be in other formats
  if (!token && data?.authorization) {
    token = data.authorization;
  }
  if (!token && typeof data === 'string') {
    token = data;
  }

  devLog("💾 Setting token:", token ? token.substring(0, 20) + "..." : "NO TOKEN FOUND");
  
  if (token) localStorage.setItem("auth_token", token);
  if (user)  localStorage.setItem("auth_user", JSON.stringify(user));

  if (!user?.id && !data?.id) {
    devLog("[login] Raw API response (no id):", data);
  }
  return data;
}

/**
 * SOCIAL LOGIN
 * Redirects or calls the social auth endpoint
 */
export async function socialLogin(provider) {
  // Most Laravel Socialite setups work by redirecting to a URL
  // that then redirects to the provider. 
  // Adjust this URL to your backend's actual social auth endpoint.
  const authUrl = `https://portal.proppins.com/api/v1/auth/${provider}/redirect`;
  window.location.href = authUrl;
}

/**
 * Logout - Call API to invalidate token on server
 */
export async function logout() {
  const { postJson } = await import("./api");
  try {
    await postJson("logout", {});
  } catch (error) {
    // Even if API call fails, we should still clear local storage
    devWarn("Logout API call failed, but clearing local storage:", error);
  }
}